import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { graphql } from '@eluve/graphql.tada';
import { DEFAULT_LANGUAGE } from '@eluve/language-utils';

import { appointmentLanguagesFragment } from './operations';
import { useIsUserAppointmentOwner } from './useIsUserAppointmentOwner';

const setAppointmentInputLanguageMutation = graphql(
  `
    mutation setAppointmentInputLanguage(
      $tenantId: uuid!
      $appointmentId: uuid!
      $inputLanguage: String!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $appointmentId }
        _set: { inputLanguage: $inputLanguage }
      ) {
        __typename
        id
        inputLanguage
      }
    }
  `,
  [],
);

const setAppointmentOutputLanguageMutation = graphql(
  `
    mutation setAppointmentOutputLanguage(
      $tenantId: uuid!
      $appointmentId: uuid!
      $outputLanguage: String!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $appointmentId }
        _set: { outputLanguage: $outputLanguage }
      ) {
        __typename
        id
        outputLanguage
      }
    }
  `,
  [],
);

type UseAppointmentLanguagesParams = {
  tenantId: string;
  appointmentId: string;
  userDefaultInputLanguage?: string;
  userDefaultOutputLanguage?: string;
  onInputLanguageChange?: (newCode: string) => void;
  onOutputLanguageChange?: (newCode: string) => void;
};

export const useAppointmentLanguages = ({
  tenantId,
  appointmentId,
  userDefaultInputLanguage,
  userDefaultOutputLanguage,
  onInputLanguageChange,
  onOutputLanguageChange,
}: UseAppointmentLanguagesParams) => {
  const isUserAppointmentOwner = useIsUserAppointmentOwner();

  const [isUpdatingInputLanguage, setIsUpdatingInputLanguage] = useState(false);
  const [isUpdatingOutputLanguage, setIsUpdatingOutputLanguage] =
    useState(false);

  const appointment = useCompleteFragment({
    fragment: appointmentLanguagesFragment,
    key: {
      id: appointmentId,
    },
  });

  const inputLanguageCode =
    appointment?.inputLanguage ?? userDefaultInputLanguage ?? DEFAULT_LANGUAGE;
  const outputLanguageCode =
    appointment?.outputLanguage ??
    userDefaultOutputLanguage ??
    DEFAULT_LANGUAGE;

  const [setAppointmentInputLanguage] = useMutation(
    setAppointmentInputLanguageMutation,
    {
      optimisticResponse: (input) => ({
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id: appointmentId,
          inputLanguage: input.inputLanguage,
        },
      }),
    },
  );

  const [setAppointmentOutputLanguage] = useMutation(
    setAppointmentOutputLanguageMutation,
    {
      optimisticResponse: (input) => ({
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id: appointmentId,
          outputLanguage: input.outputLanguage,
        },
      }),
    },
  );

  // Set languages if not already set
  useEffect(() => {
    if (!isUserAppointmentOwner) {
      return;
    }

    if (!appointment?.inputLanguage || !appointment?.outputLanguage) {
      const mutations = [];

      if (!appointment?.inputLanguage) {
        mutations.push(
          setAppointmentInputLanguage({
            variables: {
              tenantId,
              appointmentId,
              inputLanguage: inputLanguageCode,
            },
          }),
        );
      }

      if (!appointment?.outputLanguage) {
        mutations.push(
          setAppointmentOutputLanguage({
            variables: {
              tenantId,
              appointmentId,
              outputLanguage: outputLanguageCode,
            },
          }),
        );
      }

      Promise.all(mutations);
    }
  }, [
    appointment,
    appointmentId,
    inputLanguageCode,
    isUserAppointmentOwner,
    outputLanguageCode,
    setAppointmentInputLanguage,
    setAppointmentOutputLanguage,
    tenantId,
  ]);

  const preferredLanguages = [];
  if (userDefaultInputLanguage) {
    preferredLanguages.push(userDefaultInputLanguage);
  }
  if (userDefaultOutputLanguage) {
    preferredLanguages.push(userDefaultOutputLanguage);
  }

  const updateInputLanguage = async (newCode: string) => {
    if (newCode === inputLanguageCode) {
      return;
    }

    setIsUpdatingInputLanguage(true);
    try {
      const result = await setAppointmentInputLanguage({
        variables: {
          tenantId,
          appointmentId,
          inputLanguage: newCode,
        },
      });

      if (result.errors) {
        throw new Error('Failed to update input language');
      }

      onInputLanguageChange?.(newCode);
    } finally {
      setIsUpdatingInputLanguage(false);
    }
  };

  const updateOutputLanguage = async (newCode: string) => {
    if (newCode === outputLanguageCode) {
      return;
    }

    setIsUpdatingOutputLanguage(true);
    try {
      const result = await setAppointmentOutputLanguage({
        variables: {
          tenantId,
          appointmentId,
          outputLanguage: newCode,
        },
      });

      if (result.errors) {
        throw new Error('Failed to update output language');
      }

      onOutputLanguageChange?.(newCode);
    } finally {
      setIsUpdatingOutputLanguage(false);
    }
  };

  return {
    inputLanguageCode,
    outputLanguageCode,
    isUserAppointmentOwner,
    preferredLanguages,
    updateInputLanguage,
    updateOutputLanguage,
    isUpdatingInputLanguage,
    isUpdatingOutputLanguage,
  };
};
